/**
 * Admin Page
 * 
 * Lumen8 staff only - provides admin tools:
 * - Reset user passwords (temp password flow)
 * 
 * Accessible via /admin route
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

// ============================================================
// THEME (matches app theme)
// ============================================================
const THEME = {
  bg: '#0a0a0f',
  bgElevated: '#12121a',
  bgCard: '#1a1a24',
  border: '#2a2a3a',
  text: '#ffffff',
  textSecondary: '#a0a0b0',
  textMuted: '#606070',
  accent: '#2fd6b5',
  accentHover: '#26b89a',
  error: '#ef4444',
  success: '#22c55e',
  warning: '#f59e0b',
};

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    minHeight: '100vh',
    background: THEME.bg,
    padding: '40px 24px',
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
  },
  content: {
    maxWidth: 600,
    margin: '0 auto',
  },
  header: {
    marginBottom: 40,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: THEME.text,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  subtitle: {
    fontSize: 14,
    color: THEME.textSecondary,
    marginTop: 8,
  },
  card: {
    background: THEME.bgCard,
    borderRadius: 16,
    padding: 28,
    border: `1px solid ${THEME.border}`,
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: THEME.text,
    margin: '0 0 8px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardDescription: {
    fontSize: 13,
    color: THEME.textSecondary,
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: THEME.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${THEME.border}`,
    background: THEME.bgElevated,
    color: THEME.text,
    fontSize: 15,
    outline: 'none',
    transition: 'all 0.2s',
  },
  inputFocus: {
    borderColor: THEME.accent,
    boxShadow: `0 0 0 3px rgba(47, 214, 181, 0.15)`,
  },
  button: {
    padding: '14px 24px',
    borderRadius: 10,
    border: 'none',
    background: `linear-gradient(135deg, ${THEME.accent} 0%, ${THEME.accentHover} 100%)`,
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginTop: 8,
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  alert: {
    padding: '14px 16px',
    borderRadius: 10,
    fontSize: 14,
    marginTop: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  alertSuccess: {
    background: 'rgba(34, 197, 94, 0.15)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    color: THEME.success,
  },
  alertError: {
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: THEME.error,
  },
  hint: {
    fontSize: 12,
    color: THEME.textMuted,
    marginTop: 4,
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: THEME.textSecondary,
    textDecoration: 'none',
    fontSize: 14,
    marginBottom: 24,
    transition: 'color 0.2s',
  },
  accessDenied: {
    textAlign: 'center',
    padding: '80px 24px',
  },
  accessDeniedIcon: {
    fontSize: 64,
    marginBottom: 24,
  },
  accessDeniedTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: THEME.text,
    margin: '0 0 12px',
  },
  accessDeniedText: {
    fontSize: 15,
    color: THEME.textSecondary,
    margin: 0,
  },
};

// ============================================================
// COMPONENT
// ============================================================
export default function AdminPage() {
  const { isLumen8Staff, session, userEmail } = useAuth();
  
  // Password reset form state
  const [targetEmail, setTargetEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success' | 'error', message: string }
  const [focusedField, setFocusedField] = useState(null);

  // Generate a random temp password
  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // Ensure at least one number
    const numPos = Math.floor(Math.random() * password.length);
    const num = Math.floor(Math.random() * 10);
    password = password.substring(0, numPos) + num + password.substring(numPos + 1);
    setTempPassword(password);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetEmail || !tempPassword || loading) return;

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch('/api/admin/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          targetEmail: targetEmail.trim(),
          tempPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          type: 'success',
          message: data.message || 'Password reset successfully!',
        });
        // Clear form on success
        setTargetEmail('');
        setTempPassword('');
      } else {
        setResult({
          type: 'error',
          message: data.error || 'Failed to reset password',
        });
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setResult({
        type: 'error',
        message: 'Network error. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Access denied for non-Lumen8 staff
  if (!isLumen8Staff) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.accessDenied}>
            <div style={styles.accessDeniedIcon}>🔒</div>
            <h1 style={styles.accessDeniedTitle}>Access Denied</h1>
            <p style={styles.accessDeniedText}>
              This page is only accessible to Lumen8 staff.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Back link */}
        <a 
          href="/" 
          style={styles.backLink}
          onMouseEnter={(e) => e.target.style.color = THEME.accent}
          onMouseLeave={(e) => e.target.style.color = THEME.textSecondary}
        >
          ← Back to Dashboard
        </a>

        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>
            <span>⚙️</span>
            Admin Tools
          </h1>
          <p style={styles.subtitle}>
            Logged in as {userEmail}
          </p>
        </div>

        {/* Password Reset Card */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            <span>🔐</span>
            Reset User Password
          </h2>
          <p style={styles.cardDescription}>
            Set a temporary password for a user. They will be prompted to change it 
            when they next log in. Use this when a user has forgotten their password 
            and email reset isn't working.
          </p>

          <form style={styles.form} onSubmit={handleSubmit}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>User's Email</label>
              <input
                type="email"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                placeholder="user@example.com"
                style={{
                  ...styles.input,
                  ...(focusedField === 'email' ? styles.inputFocus : {}),
                }}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Temporary Password</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="TempPass123"
                  style={{
                    ...styles.input,
                    flex: 1,
                    ...(focusedField === 'password' ? styles.inputFocus : {}),
                  }}
                />
                <button
                  type="button"
                  onClick={generateTempPassword}
                  style={{
                    ...styles.input,
                    cursor: 'pointer',
                    padding: '12px 16px',
                    background: THEME.bgCard,
                    border: `1px solid ${THEME.border}`,
                    color: THEME.textSecondary,
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                  }}
                  title="Generate random password"
                >
                  🎲 Generate
                </button>
              </div>
              <p style={styles.hint}>
                Share this password securely with the user (phone, in person, etc.)
              </p>
            </div>

            <button
              type="submit"
              disabled={!targetEmail || !tempPassword || loading}
              style={{
                ...styles.button,
                ...(!targetEmail || !tempPassword || loading ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          {/* Result message */}
          {result && (
            <div style={{
              ...styles.alert,
              ...(result.type === 'success' ? styles.alertSuccess : styles.alertError),
            }}>
              <span>{result.type === 'success' ? '✓' : '✕'}</span>
              <span>{result.message}</span>
            </div>
          )}
        </div>

        {/* Instructions Card */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            <span>📋</span>
            How It Works
          </h2>
          <ol style={{ 
            margin: 0, 
            paddingLeft: 20, 
            color: THEME.textSecondary,
            fontSize: 14,
            lineHeight: 1.8,
          }}>
            <li>Enter the user's email address</li>
            <li>Generate or type a temporary password</li>
            <li>Click "Reset Password"</li>
            <li>Share the temp password with the user securely (call them)</li>
            <li>User logs in → sees "Change Password" screen</li>
            <li>User sets their own new password → done!</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
